/**
 * Recon Steps panel for the Photo List Settings page — the unified USED
 * recon-board table as a DRAG LADDER (Ian, 2026-09-02): top of the list is
 * photographed first, no priority numbers to type. Scores still exist in the
 * database; the reorder endpoint assigns them from position. Excluded steps
 * live in their own section below the ladder — "off the list" has no
 * position. Ties in the seeded defaults survive until the first drag, then
 * dissolve into the dragged order.
 *
 * Drag works with mouse AND touch via DragReorderList (pointer events).
 * Edit-in-place autosave idioms from DealerPlatesView.tsx ~384-478; add-row
 * form from WholesalePreferencesPage.tsx ~122-163.
 */
import React, { useState } from "react"
import { Trash2 } from "lucide-react"

import {
  PhotoListHighlightColor,
  PhotoListReconStep,
  createReconStep,
  deleteReconStep,
  reorderReconSteps,
  updateReconStep,
} from "../../api/photoList"
import DragReorderList, { DragHandle, DragHandleProps } from "./DragReorderList"
import { FILL } from "../../utils/photoListStyling"
import { THEME } from "../../config/theme"
import { useIsMobile } from "../../hooks/useIsMobile"

interface Props {
  steps: PhotoListReconStep[]
  /** False while the cluster is on code defaults (seed first) or read-only. */
  editable: boolean
  dealerGroupId: number
  /** Replace one row with the server's returned copy (edit-in-place). */
  onRowSaved: (row: PhotoListReconStep) => void
  /** Add/delete/reorder changed the row set — the page refetches. */
  onStructureChanged: () => void
  onError: (message: string | null) => void
}

const COLOR_OPTIONS: Array<{ value: "" | PhotoListHighlightColor; label: string }> = [
  { value: "", label: "None" },
  { value: "green", label: "Green" },
  { value: "yellow", label: "Yellow" },
  { value: "orange", label: "Orange" },
  { value: "red", label: "Red" },
]

export default function ReconStepsPanel({
  steps,
  editable,
  dealerGroupId,
  onRowSaved,
  onStructureChanged,
  onError,
}: Props) {
  const isMobile = useIsMobile()
  const [draftName, setDraftName] = useState("")
  const [adding, setAdding] = useState(false)

  const ladderSteps = steps.filter((s) => !s.excluded_from_list)
  const excludedSteps = steps.filter((s) => s.excluded_from_list)

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    const name = draftName.trim()
    if (!name) return
    setAdding(true)
    onError(null)
    try {
      await createReconStep({ name }, dealerGroupId)
      setDraftName("")
      onStructureChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to add recon step")
    } finally {
      setAdding(false)
    }
  }

  const handleReorder = async (orderedKeys: Array<number | string>) => {
    onError(null)
    try {
      await reorderReconSteps(orderedKeys as number[], dealerGroupId)
      onStructureChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to save the new order")
      onStructureChanged() // resync — the local order no longer matches
    }
  }

  return (
    <section style={panelStyle}>
      <div style={panelTitleStyle}>Recon Steps — USED sheets</div>
      <p style={panelSubtitleStyle}>
        Drag steps into shoot order — top of the ladder is photographed first.
        Color highlights the car's row on screen, print, and the Excel export.
        Excluded steps leave the car off the list entirely (Lee Bros inverts
        exclusions — that rule stays built in). A step not listed here behaves
        as unknown: no color, bottom of the list, never dropped.
      </p>

      <DragReorderList
        items={ladderSteps}
        getKey={(step) => step.id ?? step.name}
        onReorder={handleReorder}
        disabled={!editable}
        renderItem={(step, handleProps) => (
          <ReconStepRow
            step={step}
            editable={editable && step.id !== null}
            isMobile={isMobile}
            dealerGroupId={dealerGroupId}
            handleProps={handleProps}
            onRowSaved={onRowSaved}
            onStructureChanged={onStructureChanged}
            onError={onError}
          />
        )}
      />

      {editable && (
        <form onSubmit={handleAdd} style={addFormStyle(isMobile)}>
          <input
            type="text"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            placeholder="New recon step (exactly as the board spells it)"
            style={{ ...inputStyle, flex: 1 }}
          />
          <button
            type="submit"
            disabled={!draftName.trim() || adding}
            style={addButtonStyle(!draftName.trim() || adding)}
          >
            {adding ? "Adding…" : "Add Step"}
          </button>
        </form>
      )}

      {excludedSteps.length > 0 && (
        <>
          <div style={excludedHeaderStyle}>
            Excluded — these steps keep their cars off the list
          </div>
          {excludedSteps.map((step) => (
            <ReconStepRow
              key={step.id ?? `default-${step.name}`}
              step={step}
              editable={editable && step.id !== null}
              isMobile={isMobile}
              dealerGroupId={dealerGroupId}
              handleProps={null}
              onRowSaved={onRowSaved}
              onStructureChanged={onStructureChanged}
              onError={onError}
            />
          ))}
        </>
      )}
    </section>
  )
}

function ReconStepRow({
  step,
  editable,
  isMobile,
  dealerGroupId,
  handleProps,
  onRowSaved,
  onStructureChanged,
  onError,
}: {
  step: PhotoListReconStep
  editable: boolean
  isMobile: boolean
  dealerGroupId: number
  /** Null for excluded rows — they hold no ladder position. */
  handleProps: DragHandleProps | null
  onRowSaved: (row: PhotoListReconStep) => void
  onStructureChanged: () => void
  onError: (message: string | null) => void
}) {
  const [nameDraft, setNameDraft] = useState(step.name)
  React.useEffect(() => setNameDraft(step.name), [step.name])

  const save = async (updates: Parameters<typeof updateReconStep>[1]) => {
    if (step.id === null) return
    onError(null)
    try {
      const saved = await updateReconStep(step.id, updates, dealerGroupId)
      if ("excluded_from_list" in updates) {
        // Moving between the ladder and the excluded section changes the
        // row SET each list renders — refetch rather than patch in place.
        onStructureChanged()
      } else {
        onRowSaved(saved)
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to save recon step")
      setNameDraft(step.name)
    }
  }

  const handleNameBlur = () => {
    const name = nameDraft.trim()
    if (!name || name === step.name) {
      setNameDraft(step.name)
      return
    }
    save({ name })
  }

  const handleDelete = async () => {
    if (step.id === null) return
    const confirmed = window.confirm(
      `Delete recon step "${step.name}"? Cars carrying it will show no color, ` +
        `sink to the bottom, and stay on the list (unknown-step behavior).`,
    )
    if (!confirmed) return
    onError(null)
    try {
      await deleteReconStep(step.id, dealerGroupId)
      onStructureChanged()
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to delete recon step")
    }
  }

  const swatchColor = step.highlight_color ? FILL[step.highlight_color] : undefined
  const colorSelect = (
    // minWidth: 0 lets the select shrink below its intrinsic option width —
    // without it the mobile row overflows a 390px viewport.
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        ...(isMobile && { flex: 1, minWidth: 0 }),
      }}
    >
      <span style={swatchStyle(swatchColor)} aria-hidden />
      <select
        value={step.highlight_color ?? ""}
        disabled={!editable}
        onChange={(e) =>
          save({ highlight_color: (e.target.value || null) as PhotoListHighlightColor | null })
        }
        style={{ ...selectStyle, flex: 1, minWidth: 0 }}
      >
        {COLOR_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </span>
  )

  const excludedCheckbox = (
    <input
      type="checkbox"
      checked={step.excluded_from_list}
      disabled={!editable}
      onChange={(e) => save({ excluded_from_list: e.target.checked })}
      aria-label={`Exclude ${step.name} from the list`}
    />
  )

  const trashButton = editable ? (
    <button
      type="button"
      onClick={handleDelete}
      aria-label={`Delete ${step.name}`}
      style={trashButtonStyle}
    >
      <Trash2 size={15} strokeWidth={2} />
    </button>
  ) : (
    <span />
  )

  const nameInput = (
    <input
      type="text"
      value={nameDraft}
      disabled={!editable}
      onChange={(e) => setNameDraft(e.target.value)}
      onBlur={handleNameBlur}
      onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
      style={{ ...inputStyle, width: "100%", minWidth: 0 }}
    />
  )

  const dragHandle = handleProps && editable ? <DragHandle {...handleProps} /> : <span />

  if (isMobile) {
    return (
      <div style={mobileRowStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: THEME.spacing.xs }}>
          {dragHandle}
          {nameInput}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: THEME.spacing.sm, flexWrap: "wrap" }}>
          {colorSelect}
          <label style={mobileCheckboxLabelStyle}>
            {excludedCheckbox} Excluded
          </label>
          {trashButton}
        </div>
      </div>
    )
  }

  return (
    <div style={rowStyle}>
      {dragHandle}
      {nameInput}
      {colorSelect}
      <span style={{ textAlign: "center" }}>{excludedCheckbox}</span>
      {trashButton}
    </div>
  )
}

// --- Local styles (panel chrome from DealerPlatesView.tsx ~lines 500-519) ---

const panelStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  padding: THEME.spacing.md,
  marginBottom: THEME.spacing.lg,
}

const panelTitleStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 500,
  color: THEME.colors.neutral.light.textPrimary,
  marginBottom: 4,
}

const panelSubtitleStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: 0,
  marginBottom: THEME.spacing.md,
  lineHeight: 1.4,
}

const ROW_GRID = "28px minmax(180px, 1fr) 190px 90px 40px"

const rowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: ROW_GRID,
  gap: THEME.spacing.sm,
  alignItems: "center",
  padding: `${THEME.spacing.xs} 0`,
  borderTop: `1px solid ${THEME.colors.silver}`,
}

const mobileRowStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.xs,
  padding: `${THEME.spacing.sm} 0`,
  borderTop: `1px solid ${THEME.colors.silver}`,
}

const mobileCheckboxLabelStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 13,
  color: THEME.colors.neutral.light.textPrimary,
}

const excludedHeaderStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: THEME.spacing.lg,
  marginBottom: THEME.spacing.xs,
}

const inputStyle: React.CSSProperties = {
  boxSizing: "border-box",
  padding: `6px ${THEME.spacing.sm}`,
  fontSize: 13,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.sm,
  fontFamily: THEME.fonts.primary,
  background: THEME.colors.white,
}

const selectStyle: React.CSSProperties = {
  boxSizing: "border-box",
  padding: `6px ${THEME.spacing.sm}`,
  fontSize: 13,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.sm,
  fontFamily: THEME.fonts.primary,
  background: THEME.colors.white,
}

const swatchStyle = (color: string | undefined): React.CSSProperties => ({
  width: 12,
  height: 12,
  borderRadius: 3,
  border: `1px solid ${THEME.colors.silver}`,
  background: color ?? "transparent",
  flexShrink: 0,
})

const trashButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "2px 6px",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  color: THEME.colors.semantic.danger,
}

const addFormStyle = (isMobile: boolean): React.CSSProperties => ({
  display: "flex",
  flexDirection: isMobile ? "column" : "row",
  gap: THEME.spacing.sm,
  marginTop: THEME.spacing.md,
})

const addButtonStyle = (disabled: boolean): React.CSSProperties => ({
  padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
  fontSize: 13,
  fontWeight: 500,
  background: THEME.colors.navy,
  color: THEME.colors.white,
  border: "none",
  borderRadius: THEME.borderRadius.md,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.5 : 1,
  fontFamily: THEME.fonts.primary,
  whiteSpace: "nowrap",
})
